import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { syllabusScanService, SCAN_STATUS } from '../../services/SyllabusScanService';

/**
 * Dashboard banner that shows when a syllabus scan is in progress or completed.
 * Listens to SyllabusScanService events.
 */
export default function SyllabusScanBanner() {
  const navigate = useNavigate();
  const [activeScans, setActiveScans] = useState(() => syllabusScanService.getActiveScans());
  const [completedScans, setCompletedScans] = useState([]);

  useEffect(() => {
    const handleChange = (scanId, scan) => {
      if (scan.status === SCAN_STATUS.COMPLETE) {
        setCompletedScans(prev => {
          if (prev.some(s => s.scanId === scanId)) return prev;
          return [...prev, scan];
        });
        setActiveScans(syllabusScanService.getActiveScans());
      } else if (scan.status === SCAN_STATUS.PROCESSING || scan.status === SCAN_STATUS.PENDING) {
        setActiveScans(syllabusScanService.getActiveScans());
      } else if (scan.status === SCAN_STATUS.ERROR) {
        setActiveScans(syllabusScanService.getActiveScans());
      }
    };

    const unsub = syllabusScanService.on('change', handleChange);
    return unsub;
  }, []);

  const handleReviewScan = useCallback((scan) => {
    // Navigate to course wizard with the completed scan result
    navigate('/create-course-wizard', { state: { scanResult: scan.result, documentId: scan.documentId } });
    // Clear the completed scan
    syllabusScanService.clearScan(scan.scanId);
    setCompletedScans(prev => prev.filter(s => s.scanId !== scan.scanId));
  }, [navigate]);

  const handleDismiss = useCallback((scanId) => {
    syllabusScanService.clearScan(scanId);
    setCompletedScans(prev => prev.filter(s => s.scanId !== scanId));
  }, []);

  if (activeScans.length === 0 && completedScans.length === 0) return null;

  return (
    <div style={{ padding: '0 16px', marginBottom: '8px' }}>
      {activeScans.map(scan => (
        <div
          key={scan.scanId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          <div style={{
            width: '16px', height: '16px',
            border: '2px solid #e2e4e9', borderTopColor: '#6366f1',
            borderRadius: '50%', animation: 'onboarding-spin 0.8s linear infinite',
          }} />
          <span>Syllabus scan in progress — we'll let you know when it's ready.</span>
        </div>
      ))}

      {completedScans.map(scan => (
        <div
          key={scan.scanId}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '10px 16px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          <span>✓ Syllabus scan complete!</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleReviewScan(scan)}
              style={{
                padding: '4px 12px', borderRadius: '6px', fontSize: '13px',
                fontWeight: '600', background: '#10b981', color: '#fff',
                border: 'none', cursor: 'pointer',
              }}
            >
              Review & Create Course
            </button>
            <button
              onClick={() => handleDismiss(scan.scanId)}
              style={{
                padding: '4px 8px', background: 'none', border: 'none',
                cursor: 'pointer', color: '#6b7280', fontSize: '16px',
              }}
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
